/**
 * Geodetic utilities for converting between geographic coordinates (latitude,
 * longitude, altitude) and local ENU (East-North-Up) coordinates in meters.
 *
 * TypeScript port of gs-viewer/src/Geodetic.js so app code (tools, overlays,
 * measurement) doesn't depend on the gs-viewer package for coordinate math —
 * the Spark rendering path has no geodetic support of its own.
 *
 * Uses WGS84 ellipsoid parameters.
 */

export interface ENUCoordinates {
  east: number;
  north: number;
  up: number;
}

export interface GeodeticCoordinates {
  lat: number;
  lon: number;
  alt: number;
}

// WGS84 ellipsoid constants
const WGS84_A = 6378137.0; // Semi-major axis in meters
const WGS84_E2 = 0.00669437999014; // First eccentricity squared

function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180.0;
}

function radiansToDegrees(radians: number): number {
  return (radians * 180.0) / Math.PI;
}

/**
 * Convert geodetic coordinates (latitude, longitude, altitude) to ECEF
 * (Earth-Centered, Earth-Fixed) coordinates in meters.
 */
function geodeticToECEF(lat: number, lon: number, alt: number): { x: number; y: number; z: number } {
  const latRad = degreesToRadians(lat);
  const lonRad = degreesToRadians(lon);

  const sinLat = Math.sin(latRad);
  const cosLat = Math.cos(latRad);
  const sinLon = Math.sin(lonRad);
  const cosLon = Math.cos(lonRad);

  // Radius of curvature in the prime vertical
  const N = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);

  const x = (N + alt) * cosLat * cosLon;
  const y = (N + alt) * cosLat * sinLon;
  const z = (N * (1 - WGS84_E2) + alt) * sinLat;

  return { x, y, z };
}

/**
 * Convert ECEF coordinates to geodetic (latitude/longitude in degrees,
 * altitude in meters). Uses Bowring's iterative method, which converges
 * quickly (typically 2-3 iterations).
 */
function ECEFToGeodetic(x: number, y: number, z: number): GeodeticCoordinates {
  const p = Math.sqrt(x * x + y * y);
  const lon = Math.atan2(y, x);

  // Initial latitude estimate
  let lat = Math.atan2(z, p * (1 - WGS84_E2));

  // Iterate to refine latitude (Bowring's method)
  for (let i = 0; i < 5; i++) {
    const sinLatIter = Math.sin(lat);
    const NIter = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLatIter * sinLatIter);
    lat = Math.atan2(z + WGS84_E2 * NIter * sinLatIter, p);
  }

  // Compute altitude
  const sinLat = Math.sin(lat);
  const cosLat = Math.cos(lat);
  const N = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);

  let alt: number;
  if (Math.abs(cosLat) > 1e-10) {
    alt = p / cosLat - N;
  } else {
    // Near poles, use z component
    alt = Math.abs(z) / Math.abs(sinLat) - N * (1 - WGS84_E2);
  }

  return {
    lat: radiansToDegrees(lat),
    lon: radiansToDegrees(lon),
    alt,
  };
}

/**
 * Convert a position from geodetic coordinates to ENU (East-North-Up)
 * coordinates in meters relative to an observer position.
 */
export function geodeticToENU(
  lat: number,
  lon: number,
  alt: number,
  observerLat: number,
  observerLon: number,
  observerAlt: number
): ENUCoordinates {
  // Convert both positions to ECEF
  const target = geodeticToECEF(lat, lon, alt);
  const observer = geodeticToECEF(observerLat, observerLon, observerAlt);

  // Compute ECEF difference
  const dx = target.x - observer.x;
  const dy = target.y - observer.y;
  const dz = target.z - observer.z;

  // Convert observer lat/lon to radians for rotation matrix
  const latRad = degreesToRadians(observerLat);
  const lonRad = degreesToRadians(observerLon);

  const sinLat = Math.sin(latRad);
  const cosLat = Math.cos(latRad);
  const sinLon = Math.sin(lonRad);
  const cosLon = Math.cos(lonRad);

  // Rotate ECEF difference to ENU
  const east = -sinLon * dx + cosLon * dy;
  const north = -sinLat * cosLon * dx - sinLat * sinLon * dy + cosLat * dz;
  const up = cosLat * cosLon * dx + cosLat * sinLon * dy + sinLat * dz;

  return { east, north, up };
}

/**
 * Convert a position from ENU (East-North-Up) coordinates to geodetic
 * coordinates relative to an observer position. Inverse of geodeticToENU.
 */
export function ENUToGeodetic(
  east: number,
  north: number,
  up: number,
  observerLat: number,
  observerLon: number,
  observerAlt: number
): GeodeticCoordinates {
  // Convert observer position to ECEF
  const observer = geodeticToECEF(observerLat, observerLon, observerAlt);

  // Convert observer lat/lon to radians for rotation matrix
  const latRad = degreesToRadians(observerLat);
  const lonRad = degreesToRadians(observerLon);

  const sinLat = Math.sin(latRad);
  const cosLat = Math.cos(latRad);
  const sinLon = Math.sin(lonRad);
  const cosLon = Math.cos(lonRad);

  // Rotate ENU to ECEF difference (transpose of ENU rotation matrix)
  const dx = -sinLon * east - sinLat * cosLon * north + cosLat * cosLon * up;
  const dy = cosLon * east - sinLat * sinLon * north + cosLat * sinLon * up;
  const dz = cosLat * north + sinLat * up;

  // Add to observer ECEF to get target ECEF, then convert back to geodetic
  return ECEFToGeodetic(observer.x + dx, observer.y + dy, observer.z + dz);
}
